import { Briefcase, Lightbulb, Layers, BarChart3, MessageSquare, Users, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CTA from "@/components/CTA";
import { useI18n } from "@/i18n/I18nProvider";

const TRACK_ICONS = [Briefcase, Lightbulb, BarChart3, MessageSquare, Layers, Users];

export default function ForBusinessPage() {
  const { t } = useI18n();
  const tracks = t("forBusiness.tracks.items") || [];
  const approach = t("forBusiness.approach.items") || [];

  return (
    <>
      <PageHero
        eyebrow={t("forBusiness.hero.eyebrow")}
        title={t("forBusiness.hero.title")}
        description={t("forBusiness.hero.description")}
      />

      {/* THESIS */}
      <section data-testid="business-thesis" className="bg-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">{t("forBusiness.thesis.eyebrow")}</p>
            <h2 className="text-3xl md:text-[40px] font-light tracking-tight leading-[1.1] text-brand-charcoal">
              {t("forBusiness.thesis.title")}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[17px] leading-[1.75] text-brand-charcoal/90">
              {t("forBusiness.thesis.paragraph")}
            </p>
          </div>
        </div>
      </section>

      {/* TRACKS */}
      <section data-testid="business-tracks" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("forBusiness.tracks.eyebrow")}
            title={t("forBusiness.tracks.title")}
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {tracks.map((track, idx) => {
              const Icon = TRACK_ICONS[idx] || Briefcase;
              return (
                <article
                  key={idx}
                  data-testid={`track-${idx + 1}`}
                  className="group rounded-2xl bg-white border border-black/[0.06] p-8 md:p-10 soft-shadow soft-shadow-hover hover-lift"
                >
                  <div className="flex items-start justify-between mb-8">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-paper border border-brand-line">
                      <Icon className="w-5 h-5 text-brand-charcoal" strokeWidth={1.4} />
                    </span>
                    <span className="text-[10.5px] tracking-[0.18em] uppercase font-medium border rounded-full px-2.5 py-1 bg-brand-blue/10 text-brand-blue border-brand-blue/20">
                      {track.tag}
                    </span>
                  </div>
                  <h3 className="text-[22px] sm:text-[26px] font-light tracking-tight text-brand-charcoal leading-tight">
                    {track.title}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-brand-muted">{track.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEXT AS DATA */}
      <section data-testid="business-text-data" className="bg-brand-charcoal text-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow-light mb-4">{t("forBusiness.text_as_data.eyebrow")}</p>
            <h2 className="text-3xl md:text-[40px] font-light tracking-tight leading-[1.1] text-brand-off">
              {t("forBusiness.text_as_data.title")}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[17px] leading-[1.75] text-white/80">
              {t("forBusiness.text_as_data.paragraph")}
            </p>
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section data-testid="business-approach" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("forBusiness.approach.eyebrow")}
            title={t("forBusiness.approach.title")}
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {approach.map((a) => (
              <article
                key={a.num}
                data-testid={`approach-${a.num}`}
                className="rounded-2xl bg-white border border-black/[0.06] p-8 soft-shadow hover-lift soft-shadow-hover"
              >
                <span className="text-[11px] tracking-[0.2em] uppercase text-brand-muted">{a.num}</span>
                <h3 className="mt-4 text-[22px] font-medium text-brand-charcoal leading-tight">{a.title}</h3>
                <p className="mt-3 text-[14.5px] text-brand-muted leading-relaxed">{a.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CASE */}
      <section data-testid="business-case" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x">
          <div className="rounded-2xl bg-white border border-black/[0.06] p-8 md:p-12 soft-shadow grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4">
              <p className="eyebrow mb-3">{t("forBusiness.case.eyebrow")}</p>
              <h3 className="text-2xl md:text-[28px] font-light tracking-tight text-brand-charcoal leading-tight">
                {t("forBusiness.case.title")}
              </h3>
            </div>
            <div className="lg:col-span-8">
              <p className="text-[16px] leading-[1.75] text-brand-charcoal/90">
                {t("forBusiness.case.description")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIT CTA */}
      <section data-testid="business-audit-cta" className="bg-brand-charcoal text-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow-light mb-4">{t("forBusiness.audit_cta.eyebrow")}</p>
            <h2 className="text-3xl md:text-[44px] font-light tracking-tight leading-[1.05] text-brand-off">
              {t("forBusiness.audit_cta.title")}
            </h2>
            <p className="mt-5 text-white/70 text-[16px] max-w-2xl leading-relaxed">
              {t("forBusiness.audit_cta.description")}
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <CTA to="/audit" variant="solid-light" testid="cta-audit-business">
              {t("forBusiness.audit_cta.cta")}
              <ArrowRight className="w-3.5 h-3.5" />
            </CTA>
          </div>
        </div>
      </section>
    </>
  );
}
